import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Output,
  ViewContainerRef,
  booleanAttribute,
  input,
  model,
  signal,
} from '@angular/core';

@Directive({
  selector: '[ClickOutside]',
  standalone: true,
})
class ClickOutsideDirective {
  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @Output() outside = new EventEmitter<MouseEvent>();

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: any): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.outside.emit(event);
    }
  }
}

export type DropdownOption = {
  text: string;
  value?: any;
  handler?: () => void;
};

@Component({
  selector: 'app-dropdown-directive',
  standalone: true,
  imports: [ClickOutsideDirective, CommonModule],
  template: `
    @if (isOpen()) {
    <div
      (outside)="handleDismiss()"
      ClickOutside
      [ngClass]="expand() ? 'w-full' : 'w-max'"
      class="wrapper"
    >
      @for (option of options(); track $index) {
      <p
        (click)="handleOptionClick(option)"
        class="p-2 px-3 text-gray-800 transition-colors rounded-xl cursor-pointer hover:bg-gray-100"
      >
        {{ option.text }}
      </p>
      }
    </div>
    }
  `,
  styles: `
  .wrapper {
    @apply xl:flex xl:flex-col absolute left-0 right-0 gap-1 bg-white p-2 border border-gray-100 rounded-xl shadow-md;
  }
  `,
})
class DropdownDirectiveComponent {
  @HostBinding('class') get class() {
    return `absolute top-[calc(100%+4px)] z-10 w-full h-max`;
  }
  @Output() dismiss = new EventEmitter<void>();

  options = input<DropdownOption[]>([]);
  expand = input(false, { transform: booleanAttribute });

  isOpen = signal(true);

  value = model<any>(null);

  handleDismiss() {
    this.isOpen.set(false);
    setTimeout(() => this.dismiss.emit(), 200);
  }

  handleOptionClick(option: DropdownOption) {
    const { handler, value } = option;

    this.value.set(value);

    if (handler) {
      handler();
    }

    this.handleDismiss();
  }
}

@Directive({
  selector: '[dropdown]',
  standalone: true,
})
export class DropdownDirective {
  value = model<any>();
  options = input<DropdownOption[]>([]);
  title = input<string>('');
  expand = input(false, { transform: booleanAttribute });

  @HostBinding('class') class = 'cursor-pointer select-none relative';
  @HostListener('click', ['$event']) click(event: MouseEvent) {
    this.open();
  }

  constructor(
    private readonly changeRef: ChangeDetectorRef,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  close() {
    this.viewContainerRef.clear();
  }

  open() {
    this.openDropDown();
  }

  openDropDown() {
    const componentRef = this.viewContainerRef.createComponent(
      DropdownDirectiveComponent
    );
    componentRef.setInput('options', this.options());
    componentRef.setInput('expand', this.expand());
    this.viewContainerRef.element.nativeElement.appendChild(
      componentRef.location.nativeElement
    );
    this.changeRef.detectChanges();

    componentRef.instance.dismiss.subscribe(() => {
      if (componentRef.instance.value()) {
        this.value.set(componentRef.instance.value());
      }
      this.viewContainerRef.clear();
    });
  }
}
